import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { checkUcumCode, suggestUnitDisplay, UNIT_ERRORS } from '@wodi/domain';
import { LAB_RULE_KINDS, type LabAnalyteView, type LabRuleView } from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  DialogActions,
  EmptyState,
  Notice,
  RadioGroup,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  Section,
} from '@wodi/ui';
import { useState } from 'react';

import {
  addLabAnalyte,
  approveLabRule,
  labAnalytesQuery,
  labKeys,
  labRulesQuery,
  putLabAnalyteOptions,
  putLabRules,
  updateLabAnalyte,
} from '../api/lab.ts';
import { valueSetQuery } from '../api/registration-config.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../lab/lab.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { LabSettingsFrame, QueryState } from './settings-lab.tsx';

const RESULT_TYPE_LABELS = {
  numeric: 'Number',
  coded: 'Choice from a list',
  text: 'Text',
} as const;

/** The analytes the lab measures; one analyte can be a component of several tests. */
export function LabAnalytesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const analytes = useQuery(labAnalytesQuery);
  const [adding, setAdding] = useState(false);
  const canEdit = session !== null && can(session, 'lab.catalogue.manage');
  return (
    <LabSettingsFrame title="Lab">
      {canEdit ? (
        <div className={styles.actions}>
          <Button
            onPress={() => {
              setAdding(true);
            }}
          >
            Add analyte
          </Button>
        </div>
      ) : null}
      <QueryState query={analytes} what="analytes" />
      {analytes.isSuccess && analytes.data.length === 0 ? (
        <EmptyState>
          No analytes yet. Add the things the lab measures, then build tests from them.
        </EmptyState>
      ) : null}
      {analytes.isSuccess && analytes.data.length > 0 ? (
        <Table aria-label="Analytes" dense>
          <TableHeader>
            <Column isRowHeader>Analyte</Column>
            <Column>Short name</Column>
            <Column>Result</Column>
            <Column>Unit</Column>
            <Column>LOINC</Column>
            <Column>Status</Column>
          </TableHeader>
          <TableBody>
            {analytes.data.map((a) => (
              <Row key={a.id}>
                <Cell>
                  <Link to="/settings/lab/analytes/$analyteId" params={{ analyteId: a.id }}>
                    {a.name}
                  </Link>
                </Cell>
                <Cell>{a.shortName}</Cell>
                <Cell>{RESULT_TYPE_LABELS[a.resultType]}</Cell>
                <Cell>{a.unit?.display ?? ''}</Cell>
                <Cell>{a.loinc === null ? 'Not mapped' : a.loinc.code}</Cell>
                <Cell>
                  <Tag state={a.status === 'active' ? 'done' : 'pending'}>
                    {a.status === 'active' ? 'Active' : 'Switched off'}
                  </Tag>
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {adding ? (
        <AddAnalyteDialog
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </LabSettingsFrame>
  );
}

function unitError(code: string): string | undefined {
  if (code.trim() === '') return undefined;
  const checked = checkUcumCode(code.trim());
  return checked.ok ? undefined : UNIT_ERRORS[checked.code];
}

function decimalsError(text: string): string | undefined {
  const n = Number(text);
  return text.trim() === '' || !Number.isInteger(n) || n < 0 || n > 4
    ? 'Enter 0 to 4 decimal places.'
    : undefined;
}

function AddAnalyteDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [resultType, setResultType] = useState<'numeric' | 'coded' | 'text'>('numeric');
  const [unitCode, setUnitCode] = useState('');
  const [unitDisplay, setUnitDisplay] = useState('');
  const [displayTouched, setDisplayTouched] = useState(false);
  const [decimals, setDecimals] = useState('');
  const [allowsNegative, setAllowsNegative] = useState(false);
  const [options, setOptions] = useState('');
  const [tried, setTried] = useState(false);

  const numeric = resultType === 'numeric';
  const answers = options
    .split('\n')
    .map((o) => o.trim())
    .filter((o) => o !== '');
  const errors = {
    name: name.trim() === '' ? 'Enter the analyte name.' : undefined,
    shortName: shortName.trim() === '' ? 'Enter a short name for labels.' : undefined,
    unit: numeric
      ? unitCode.trim() === ''
        ? 'Enter the unit as a UCUM code, for example mmol/L.'
        : unitError(unitCode)
      : undefined,
    decimals: numeric ? decimalsError(decimals) : undefined,
    options:
      resultType === 'coded' && answers.length === 0 ? 'Enter at least one answer.' : undefined,
  };
  const add = useMutation({
    mutationFn: () =>
      addLabAnalyte({
        name: name.trim(),
        shortName: shortName.trim(),
        resultType,
        unit: numeric ? { code: unitCode.trim(), display: unitDisplay.trim() } : null,
        decimals: numeric ? Number(decimals) : null,
        allowsNegative: numeric ? allowsNegative : false,
        options: resultType === 'coded' ? answers : [],
      }),
    onSuccess: async (analyte) => {
      toasts.show({ title: 'Analyte added', description: analyte.name });
      await queryClient.invalidateQueries({ queryKey: labKeys.catalogue });
      onClose();
    },
  });
  const shown = (key: keyof typeof errors) =>
    errors[key] !== undefined && (tried || key === 'unit') ? { errorMessage: errors[key] } : {};
  return (
    <Dialog
      title="Add analyte"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <TextField label="Name" value={name} onChange={setName} {...shown('name')} />
        <TextField
          label="Short name"
          description="For labels and narrow columns, up to 20 characters."
          value={shortName}
          onChange={setShortName}
          {...shown('shortName')}
        />
        <RadioGroup
          label="Result"
          value={resultType}
          options={(['numeric', 'coded', 'text'] as const).map((t) => ({
            id: t,
            label: RESULT_TYPE_LABELS[t],
          }))}
          onChange={(v) => {
            setResultType(v as typeof resultType);
          }}
        />
        {numeric ? (
          <>
            <div className={styles.row}>
              <TextField
                label="Unit (UCUM code)"
                value={unitCode}
                onChange={(code) => {
                  setUnitCode(code);
                  if (!displayTouched) setUnitDisplay(suggestUnitDisplay(code.trim()));
                }}
                {...(unitCode.trim() !== '' || tried ? shown('unit') : {})}
              />
              <TextField
                label="Unit as printed"
                value={unitDisplay}
                onChange={(display) => {
                  setUnitDisplay(display);
                  setDisplayTouched(true);
                }}
              />
            </div>
            <TextField
              label="Decimal places"
              inputMode="numeric"
              value={decimals}
              onChange={setDecimals}
              {...shown('decimals')}
            />
            <Checkbox isSelected={allowsNegative} onChange={setAllowsNegative}>
              Can be negative
            </Checkbox>
          </>
        ) : null}
        {resultType === 'coded' ? (
          <TextArea
            label="Answers, one per line"
            description="The clinical lead marks which answers are abnormal or critical."
            value={options}
            onChange={setOptions}
            rows={4}
            {...shown('options')}
          />
        ) : null}
        {add.isError ? (
          <Notice tone="error">
            {errorText(add.error, "The analyte wasn't added. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={add.isPending}
          pendingLabel="Adding…"
          isDisabled={!online}
          onPress={() => {
            setTried(true);
            if (Object.values(errors).every((e) => e === undefined)) add.mutate();
          }}
        >
          Add analyte
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const analyteRoute = getRouteApi('/_app/settings/lab/analytes/$analyteId');

/** One analyte: its details, its answers if coded, and the clinical lead's ranges. */
export function LabAnalytePage() {
  const { analyteId } = analyteRoute.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const analytes = useQuery(labAnalytesQuery);
  const analyte = analytes.data?.find((a) => a.id === analyteId);
  return (
    <LabSettingsFrame title={analyte?.name ?? 'Analyte'}>
      <QueryState query={analytes} what="analyte" />
      {analytes.isSuccess && analyte === undefined ? (
        <Notice tone="info">
          That analyte isn't in this lab's list.{' '}
          <Link to="/settings/lab/analytes">See analytes</Link>.
        </Notice>
      ) : null}
      {analyte === undefined || session === null ? null : (
        <>
          {can(session, 'lab.catalogue.manage') ? (
            <AnalyteDetails key={analyte.version} analyte={analyte} />
          ) : (
            <p className={styles.meta}>
              {RESULT_TYPE_LABELS[analyte.resultType]}
              {analyte.unit === null ? '' : `, ${analyte.unit.display}`}
            </p>
          )}
          {analyte.resultType === 'coded' && can(session, 'lab.catalogue.manage') ? (
            <AnswerOptions key={`o-${String(analyte.version)}`} analyte={analyte} />
          ) : null}
          {analyte.resultType === 'numeric' ? <Ranges analyte={analyte} /> : null}
        </>
      )}
    </LabSettingsFrame>
  );
}

function AnalyteDetails({ analyte }: { analyte: LabAnalyteView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [name, setName] = useState(analyte.name);
  const [shortName, setShortName] = useState(analyte.shortName);
  const [unitCode, setUnitCode] = useState(analyte.unit?.code ?? '');
  const [unitDisplay, setUnitDisplay] = useState(analyte.unit?.display ?? '');
  const [decimals, setDecimals] = useState(
    analyte.decimals === null ? '' : String(analyte.decimals),
  );
  const [active, setActive] = useState(analyte.status === 'active');
  const numeric = analyte.resultType === 'numeric';
  const unitChanged =
    numeric && (unitCode !== (analyte.unit?.code ?? '') || decimals !== String(analyte.decimals));
  const save = useMutation({
    mutationFn: () =>
      updateLabAnalyte(analyte.id, {
        expectedVersion: analyte.version,
        name: name.trim(),
        shortName: shortName.trim(),
        ...(numeric && unitChanged
          ? {
              unit: { code: unitCode.trim(), display: unitDisplay.trim() },
              decimals: Number(decimals),
            }
          : numeric && unitDisplay !== analyte.unit?.display
            ? { unit: { code: unitCode.trim(), display: unitDisplay.trim() } }
            : {}),
        ...(active === (analyte.status === 'active')
          ? {}
          : { status: active ? 'active' : 'inactive', statusReason: 'Changed in lab settings' }),
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Analyte saved' });
      await queryClient.invalidateQueries({ queryKey: labKeys.catalogue });
    },
  });
  const unitProblem = numeric ? unitError(unitCode) : undefined;
  const decimalsProblem = numeric ? decimalsError(decimals) : undefined;
  const invalid = unitProblem !== undefined || decimalsProblem !== undefined;
  return (
    <form
      className={styles.form}
      aria-label="Analyte details"
      onSubmit={(event) => {
        event.preventDefault();
        if (!invalid) save.mutate();
      }}
    >
      <div className={styles.row}>
        <TextField label="Name" value={name} onChange={setName} />
        <TextField label="Short name" value={shortName} onChange={setShortName} />
      </div>
      {numeric ? (
        <>
          <div className={styles.row}>
            <TextField
              label="Unit (UCUM code)"
              value={unitCode}
              onChange={setUnitCode}
              isDisabled={analyte.inUse}
              {...(unitProblem === undefined ? {} : { errorMessage: unitProblem })}
            />
            <TextField label="Unit as printed" value={unitDisplay} onChange={setUnitDisplay} />
            <TextField
              label="Decimal places"
              value={decimals}
              onChange={setDecimals}
              {...(decimalsProblem === undefined ? {} : { errorMessage: decimalsProblem })}
            />
          </div>
          {analyte.inUse ? (
            <p className={styles.meta}>
              Results exist in this unit, so the unit code can't change. Add a new analyte instead.
            </p>
          ) : null}
          {unitChanged ? (
            <Notice tone="info">
              Changing the unit or decimals sends this analyte's ranges back to provisional for the
              clinical lead to approve again.
            </Notice>
          ) : null}
        </>
      ) : null}
      <p className={styles.meta}>
        {analyte.loinc === null
          ? 'LOINC: not mapped. Mapping is optional; results store LOINC when it is set.'
          : `LOINC: ${analyte.loinc.code} ${analyte.loinc.display}`}
      </p>
      <Checkbox isSelected={active} onChange={setActive}>
        In use
      </Checkbox>
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, "The analyte wasn't saved. Try again.")}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          isPending={save.isPending}
          pendingLabel="Saving…"
          isDisabled={!online}
        >
          Save analyte
        </Button>
      </div>
    </form>
  );
}

function AnswerOptions({ analyte }: { analyte: LabAnalyteView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [options, setOptions] = useState(
    analyte.options.map((o) => ({
      code: o.code,
      display: o.display,
      active: o.status === 'active',
    })),
  );
  const [adding, setAdding] = useState('');
  const save = useMutation({
    mutationFn: () =>
      putLabAnalyteOptions(
        analyte.id,
        analyte.version,
        options.map((o) => ({
          ...(o.code === '' ? {} : { code: o.code }),
          display: o.display,
          status: o.active ? ('active' as const) : ('inactive' as const),
        })),
      ),
    onSuccess: async () => {
      toasts.show({ title: 'Answers saved' });
      await queryClient.invalidateQueries({ queryKey: labKeys.catalogue });
    },
  });
  return (
    <Section title="Answers">
      <p className={styles.meta}>
        Answers are never deleted: old results still show them. Switch one off to stop it being
        chosen. Whether an answer is abnormal or critical is set by the clinical lead.
      </p>
      <ul className={styles.list}>
        {options.map((o, index) => (
          <li key={o.code === '' ? `new-${String(index)}` : o.code} className={styles.listRow}>
            <span>{o.display}</span>
            <span className={styles.meta}>
              {analyte.options.find((x) => x.code === o.code)?.interpretation === 'critical'
                ? 'Marked critical'
                : ''}
            </span>
            <Checkbox
              isSelected={o.active}
              onChange={(active) => {
                setOptions((list) => list.map((x, i) => (i === index ? { ...x, active } : x)));
              }}
            >
              {`${o.display} in use`}
            </Checkbox>
          </li>
        ))}
      </ul>
      <div className={styles.toolbar}>
        <TextField label="New answer" value={adding} onChange={setAdding} />
        <Button
          isDisabled={adding.trim() === ''}
          onPress={() => {
            setOptions((list) => [...list, { code: '', display: adding.trim(), active: true }]);
            setAdding('');
          }}
        >
          Add answer
        </Button>
      </div>
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, "The answers weren't saved.")}</Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          isPending={save.isPending}
          pendingLabel="Saving…"
          isDisabled={!online}
          onPress={() => {
            save.mutate();
          }}
        >
          Save answers
        </Button>
      </div>
    </Section>
  );
}

const KIND_LABELS: Record<(typeof LAB_RULE_KINDS)[number], string> = {
  reference_range: 'Reference range',
  critical_range: 'Critical range',
  possible_limits: 'Possible limits',
};

const APPROVAL_LABELS = { provisional: 'Provisional', approved: 'Approved', retired: 'Retired' };

function ages(rule: Pick<LabRuleView, 'ageFromDays' | 'ageToDays'>): string {
  return rule.ageToDays === null
    ? `From ${String(rule.ageFromDays)} days`
    : `${String(rule.ageFromDays)} to ${String(rule.ageToDays)} days`;
}

/**
 * The clinical lead's ranges for one analyte (plan §2.6, K1): the triage rules model, with
 * a sex dimension. Every range starts provisional and a second person approves it. Nothing
 * is seeded; with no range, results read "No range set" and nothing is flagged.
 */
function Ranges({ analyte }: { analyte: LabAnalyteView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const rules = useQuery(labRulesQuery(analyte.id));
  const canManage = session !== null && can(session, 'clinical_rules.manage');
  const canApprove = session !== null && can(session, 'clinical_rules.approve');
  const sexes = useQuery({ ...valueSetQuery('patient_sex'), enabled: canManage });
  const unit = analyte.unit?.display ?? '';
  const [kind, setKind] = useState<(typeof LAB_RULE_KINDS)[number]>('reference_range');
  const [from, setFrom] = useState('0');
  const [to, setTo] = useState('');
  const [sex, setSex] = useState('any');
  const [low, setLow] = useState('');
  const [high, setHigh] = useState('');
  const [source, setSource] = useState('');
  const [tried, setTried] = useState(false);

  const live = (rules.data?.rules ?? []).filter((r) => r.approval !== 'retired');
  const keep = (r: LabRuleView) => ({
    id: r.id,
    kind: r.kind,
    ageFromDays: r.ageFromDays,
    ageToDays: r.ageToDays,
    sexCode: r.sexCode,
    low: r.low,
    high: r.high,
    source: r.source,
  });
  // The response is the analyte's rules as saved; it replaces what is shown.
  const settled = (title: string) => {
    toasts.show({ title });
  };
  const replace = useMutation({
    mutationFn: (next: ReturnType<typeof keep>[]) => putLabRules(analyte.id, { rules: next }),
    onSuccess: (data) => {
      queryClient.setQueryData(labRulesQuery(analyte.id).queryKey, data);
      settled('Ranges saved');
    },
  });
  const approve = useMutation({
    mutationFn: (ruleId: string) => approveLabRule(ruleId),
    onSuccess: (data) => {
      queryClient.setQueryData(labRulesQuery(analyte.id).queryKey, data);
      settled('Range approved');
    },
  });

  const fromDays = Number(from);
  const toDays = to.trim() === '' ? null : Number(to);
  const errors = {
    from: !Number.isInteger(fromDays) || fromDays < 0 ? 'Enter whole days, 0 or more.' : undefined,
    to:
      toDays !== null && (!Number.isInteger(toDays) || toDays <= fromDays)
        ? 'Enter whole days after the start, or leave empty.'
        : undefined,
    bounds:
      low.trim() === '' && high.trim() === ''
        ? 'Enter a low bound, a high bound or both.'
        : undefined,
    source:
      source.trim().length < 3 ? 'Name the source and edition the range comes from.' : undefined,
  };
  const add = () => {
    setTried(true);
    if (Object.values(errors).some((e) => e !== undefined)) return;
    replace.mutate([
      ...live.map(keep),
      {
        kind,
        ageFromDays: fromDays,
        ageToDays: toDays,
        sexCode: sex === 'any' ? null : sex,
        low: low.trim() === '' ? null : low.trim(),
        high: high.trim() === '' ? null : high.trim(),
        source: source.trim(),
      } as ReturnType<typeof keep>,
    ]);
  };

  return (
    <Section title="Ranges">
      <p className={styles.meta}>
        Entered by the clinical lead with their source, and approved by a second person. A
        provisional range flags results and says so on screen and on the printed report.
      </p>
      <QueryState query={rules} what="ranges" />
      {rules.isSuccess && live.length === 0 ? (
        <p>No ranges set. Results read "No range set" and are not flagged.</p>
      ) : null}
      {live.length > 0 ? (
        <Table flat aria-label="Ranges" dense>
          <TableHeader>
            <Column isRowHeader>Kind</Column>
            <Column>Ages</Column>
            <Column>Sex</Column>
            <Column numeric>Low</Column>
            <Column numeric>High</Column>
            <Column>Source</Column>
            <Column>Approval</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody>
            {live.map((r) => (
              <Row key={r.id}>
                <Cell>{KIND_LABELS[r.kind]}</Cell>
                <Cell>{ages(r)}</Cell>
                <Cell>{r.sexCode ?? 'Any'}</Cell>
                <Cell numeric>{r.low ?? ''}</Cell>
                <Cell numeric>{r.high ?? ''}</Cell>
                <Cell>{r.source}</Cell>
                <Cell>
                  <Tag state={r.approval === 'approved' ? 'done' : 'pending'}>
                    {APPROVAL_LABELS[r.approval]}
                  </Tag>
                </Cell>
                <Cell>
                  <span className={styles.actions}>
                    {canApprove && r.approval === 'provisional' ? (
                      <Button
                        dense
                        isDisabled={!online}
                        isPending={approve.isPending && approve.variables === r.id}
                        pendingLabel="Approving…"
                        onPress={() => {
                          approve.mutate(r.id);
                        }}
                      >
                        Approve
                      </Button>
                    ) : null}
                    {canManage ? (
                      <Button
                        dense
                        variant="quiet"
                        isDisabled={!online}
                        aria-label={`Retire ${KIND_LABELS[r.kind]} ${ages(r)}`}
                        onPress={() => {
                          replace.mutate(live.filter((x) => x.id !== r.id).map(keep));
                        }}
                      >
                        Retire
                      </Button>
                    ) : null}
                  </span>
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {approve.isError ? (
        <Notice tone="error">{errorText(approve.error, "The range wasn't approved.")}</Notice>
      ) : null}
      {canManage ? (
        <form
          aria-label="Add a range"
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <h3 className={styles.sectionHeading}>Add a range</h3>
          <div className={styles.row}>
            <Select
              label="Kind"
              options={LAB_RULE_KINDS.map((k) => ({ id: k, label: KIND_LABELS[k] }))}
              value={kind}
              onChange={(v) => {
                if (v !== null) setKind(String(v) as typeof kind);
              }}
            />
            <Select
              label="Sex"
              options={[
                { id: 'any', label: 'Any sex' },
                ...(sexes.data ?? []).map((s) => ({ id: s.code, label: s.display })),
              ]}
              value={sex}
              onChange={(v) => {
                setSex(v === null ? 'any' : String(v));
              }}
            />
          </div>
          <div className={styles.row}>
            <TextField
              label="From age"
              unit="days"
              value={from}
              onChange={setFrom}
              {...(tried && errors.from !== undefined ? { errorMessage: errors.from } : {})}
            />
            <TextField
              label="Up to age, not included"
              unit="days"
              description="Leave empty for no upper age."
              value={to}
              onChange={setTo}
              {...(tried && errors.to !== undefined ? { errorMessage: errors.to } : {})}
            />
          </div>
          <div className={styles.row}>
            <TextField
              label="Low"
              unit={unit}
              value={low}
              onChange={setLow}
              {...(tried && errors.bounds !== undefined ? { errorMessage: errors.bounds } : {})}
            />
            <TextField label="High" unit={unit} value={high} onChange={setHigh} />
          </div>
          <TextField
            label="Source"
            description="The reference and edition, for example the analyser insert and its version."
            value={source}
            onChange={setSource}
            {...(tried && errors.source !== undefined ? { errorMessage: errors.source } : {})}
          />
          {replace.isError ? (
            <Notice tone="error">{errorText(replace.error, "The ranges weren't saved.")}</Notice>
          ) : null}
          <div className={styles.actions}>
            <Button
              type="submit"
              isPending={replace.isPending}
              pendingLabel="Saving…"
              isDisabled={!online}
            >
              Add range
            </Button>
          </div>
        </form>
      ) : null}
    </Section>
  );
}
